
import { useGlobalDiscoveryFacets } from "@entities/global-discovery/hooks/useGlobalDiscoveryFacets";
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { ConfigGlobalTypeSingle } from "@shared/lib/types";
import { Select } from "@shared/ui";
import { InputNumber } from "antd";
import { useEffect, useMemo, useState } from "react";
import styles from "./SelectRangeFilter.module.scss";

interface SelectRangeFilterProps {
  filter: ConfigGlobalTypeSingle;
  showTitle?: boolean;
  selectedFilters?: any;
  setSelectedFilter?: (updateFn: (value: any) => any) => void;
  platformId?: string;
}

export function SelectRangeFilter({
  filter,
  showTitle = true,
  selectedFilters: selectedFiltersProp,
  setSelectedFilter: setSelectedFilterProp,
  platformId: platformIdProp,
}: SelectRangeFilterProps) {
  const globalStore = useGetGlobalDiscoveryStore();
  const selectedFilters = selectedFiltersProp ?? globalStore.selectedFilters;
  const setSelectedFilter = setSelectedFilterProp ?? globalStore.setSelectedFilter;
  const platformId = platformIdProp ?? globalStore.platformId;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Use optionsList if provided, otherwise fetch from backend
  const hasOptionsList = Boolean(
    filter.optionsList && filter.optionsList.length > 0
  );

  // Fetch facet options from backend (only if no optionsList)
  const { facet, isFetching } = useGlobalDiscoveryFacets({
    facetName: filter.facetName,
    body: {},
    query: debouncedQuery,
    work_platform_id: platformId,
    enabled: !!platformId && !hasOptionsList,
  });

  // Get current selected value and min percentage
  const currentData = useMemo(() => {
    const value = selectedFilters[filter.facetName];

    if (hasOptionsList) {
      // For optionsList: structure is { value: { min, max }, min }
      if (value && typeof value === "object" && "value" in value) {
        const valueObj = value.value;
        const selectedRange =
          valueObj &&
          typeof valueObj === "object" &&
          "min" in valueObj &&
          "max" in valueObj
            ? {
                min:
                  typeof valueObj.min === "number" ? valueObj.min : undefined,
                max:
                  typeof valueObj.max === "number" ? valueObj.max : undefined,
              }
            : undefined;

        return {
          selectedRange,
          min:
            "min" in value && typeof value.min === "number"
              ? value.min
              : undefined,
        };
      }
      return { selectedRange: undefined, min: undefined };
    } else {
      // For non-optionsList: structure is { value: string, min }
      if (value && typeof value === "object" && "value" in value) {
        return {
          selectedValue: value.value as string,
          min:
            "min" in value && typeof value.min === "number"
              ? value.min
              : undefined,
        };
      }
      return { selectedValue: undefined, min: undefined };
    }
  }, [selectedFilters, filter.facetName, hasOptionsList]);

  // Get options from optionsList or from backend facets
  const options = useMemo(() => {
    let baseOptions: Array<{ label: string; value: string }> = [];

    if (hasOptionsList && filter.optionsList) {
      baseOptions = filter.optionsList.map((item) => ({
        label: item.label,
        value:
          typeof item.value === "object" && item.value !== null
            ? JSON.stringify(item.value)
            : String(item.value),
      }));
    } else if (facet) {
      baseOptions = facet.map((item) => ({
        label: item.name,
        value: item.value,
      }));
    }

    // Include selected value if it's not in current options (using label from filterTags)
    // Only for non-optionsList case
    if (!hasOptionsList) {
      const currentValue = currentData.selectedValue;
      if (
        currentValue &&
        !baseOptions.some((opt) => opt.value === currentValue)
      ) {
        const labelFromFilterTags =
          selectedFilters.filterTags?.[filter.facetName];
        baseOptions.push({
          label:
            typeof labelFromFilterTags === "string"
              ? labelFromFilterTags
              : currentValue,
          value: currentValue,
        });
      }
    }

    return baseOptions;
  }, [
    hasOptionsList,
    filter.optionsList,
    facet,
    currentData.selectedValue,
    selectedFilters.filterTags,
    filter.facetName,
  ]);

  const handleSelectChange = (value: string | undefined) => {
    setSelectedFilter((prevFilters) => {
      if (hasOptionsList) {
        // For optionsList: structure is { value: { min, max }, min }
        // filterTags will be the label from optionsList
        if (value) {
          // Find the option from optionsList to get the label
          const optionFromList = filter.optionsList?.find(
            (opt) =>
              (typeof opt.value === "object" && opt.value !== null
                ? JSON.stringify(opt.value)
                : String(opt.value)) === value
          );
          const label = optionFromList?.label || value;

          // Parse the value if it's a JSON string
          let optionValue: { min?: number; max?: number } | undefined;
          try {
            optionValue = JSON.parse(value) as { min?: number; max?: number };
          } catch {
            if (optionFromList && typeof optionFromList.value === "object") {
              optionValue = optionFromList.value as {
                min?: number;
                max?: number;
              };
            }
          }

          // Preserve existing min percentage if it exists
          const currentValue = prevFilters[filter.facetName];
          const existingMin =
            currentValue &&
            typeof currentValue === "object" &&
            "min" in currentValue
              ? (currentValue.min as number)
              : 0;

          return {
            ...prevFilters,
            [filter.facetName]: optionValue
              ? {
                  value: optionValue,
                  min: existingMin, // Preserve min percentage
                }
              : undefined,
            filterTags: {
              ...(prevFilters.filterTags || {}),
              [filter.facetName]: label, // Label from optionsList
              [`${filter.facetName}_min`]: existingMin, // Store min value
            },
          };
        }

        return {
          ...prevFilters,
          [filter.facetName]: undefined,
          filterTags: {
            ...(prevFilters.filterTags || {}),
            [filter.facetName]: undefined,
            [`${filter.facetName}_min`]: undefined, // Clear min
          },
        };
      } else {
        // For non-optionsList: structure is { value: string, min }
        // filterTags will be the label from facet (facetName)
        if (value) {
          // Find the label from options (which comes from facets)
          const selectedOption = options.find((opt) => opt.value === value);
          const label = selectedOption?.label || value;

          return {
            ...prevFilters,
            [filter.facetName]: {
              value: value,
              min: 0, // Reset min when selection changes
            },
            filterTags: {
              ...(prevFilters.filterTags || {}),
              [filter.facetName]: label, // Label from facet
              [`${filter.facetName}_min`]: 0, // Clear min when selection changes
            },
          };
        }

        return {
          ...prevFilters,
          [filter.facetName]: undefined,
          filterTags: {
            ...(prevFilters.filterTags || {}),
            [filter.facetName]: undefined,
            [`${filter.facetName}_min`]: undefined, // Clear min
          },
        };
      }
    });
    // Clear search when selection is made
    setSearchQuery("");
  };

  const handleMinChange = (minValue: number | string | null) => {
    const numValue =
      minValue !== null && typeof minValue === "number" ? minValue : undefined;

    setSelectedFilter((prevFilters) => {
      const currentValue = prevFilters[filter.facetName];
      const currentFilterTags = prevFilters.filterTags || {};
      const currentLabel = currentFilterTags[filter.facetName];

      if (hasOptionsList) {
        // For optionsList: update min in { value: { min, max }, min } structure
        const selectedRange =
          currentValue &&
          typeof currentValue === "object" &&
          "value" in currentValue
            ? (currentValue.value as { min?: number; max?: number })
            : undefined;

        if (!selectedRange) {
          // If no selected range, don't update
          return prevFilters;
        }

        return {
          ...prevFilters,
          [filter.facetName]: {
            value: selectedRange,
            min: numValue,
          },
          filterTags: {
            ...currentFilterTags,
            [filter.facetName]: currentLabel, // Preserve label
            [`${filter.facetName}_min`]: numValue, // Store min value
          },
        };
      } else {
        // For non-optionsList: update min in { value: string, min } structure
        const selectedValue =
          currentValue &&
          typeof currentValue === "object" &&
          "value" in currentValue
            ? (currentValue.value as string)
            : undefined;

        if (!selectedValue) {
          // If no selected value, don't update
          return prevFilters;
        }

        return {
          ...prevFilters,
          [filter.facetName]: {
            value: selectedValue,
            min: numValue,
          },
          filterTags: {
            ...currentFilterTags,
            [filter.facetName]: currentLabel, // Preserve label
            [`${filter.facetName}_min`]: numValue, // Store min value
          },
        };
      }
    });
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    // Clear search query when dropdown closes
    if (!open) {
      setSearchQuery("");
    }
  };

  return (
    <div className={styles.selectRangeFilter}>
      {showTitle && <label className={styles.label}>{filter.title}</label>}
      <div className={styles.inputsContainer}>
        <Select
          size="small"
          className={styles.select}
          placeholder={filter.placeHolder || "Select..."}
          options={options}
          value={
            hasOptionsList
              ? currentData.selectedRange
                ? (() => {
                    // Find the option that matches current selectedRange
                    const matchingOption = options.find((opt) => {
                      try {
                        const optValue = JSON.parse(opt.value) as {
                          min?: number;
                          max?: number;
                        };
                        return (
                          optValue.min === currentData.selectedRange?.min &&
                          optValue.max === currentData.selectedRange?.max
                        );
                      } catch {
                        return false;
                      }
                    });
                    return matchingOption?.value;
                  })()
                : undefined
              : currentData.selectedValue
          }
          onChange={handleSelectChange}
          onSearch={hasOptionsList ? undefined : handleSearch}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          searchValue={hasOptionsList ? undefined : searchQuery || undefined}
          showSearch={!hasOptionsList}
          loading={!hasOptionsList && isFetching}
          allowClear
          filterOption={false}
        />
        <InputNumber
          suffix="%"
          className={styles.inputNumber}
          placeholder="Min"
          size="small"
          value={currentData.min ?? null}
          onChange={handleMinChange}
          disabled={
            hasOptionsList
              ? !currentData.selectedRange
              : !currentData.selectedValue
          }
          min={0}
        />
      </div>
    </div>
  );
}
